// Stresstest omgång 6: tio helt nya projektprofiler bedömda ur åtta rollers
// perspektiv. Två av projekten körs hela vägen från idéstadie till
// förvaltning och rör vid varje dokument som finns i systemet.
// Omgång 6 pressar särskilt det som byggdes efter omgång 5: dagslägesrapport
// vid datum (Dok 06c), upprullade kvittenser i entreprenörsträdet,
// inbjudningsmallar för inhyrda granskare och den publika läsvyn för
// projektörspaket (/leverans/<kod>).
// Dokumentlistan hämtas från docRegistry så att matrisen alltid stämmer
// med den faktiska numreringen.
// Används i appen som läsbar rapport (Dok 00c) och som Word-export.


import { DOC_REGISTRY } from "./docRegistry";

export type Coverage = "klart" | "delvis" | "saknas";

export type AuditRole =
  | "Byggherre"
  | "Projektör"
  | "Bas-P"
  | "Bas-U"
  | "Entreprenör"
  | "Projektledare"
  | "Uppdragsledare"
  | "Förvaltare";

export interface RolePerspective {
  role: AuditRole;
  coverage: Coverage;
  /** Vad rollen får ut av systemet i den här profilen. */
  works: string;
  /** Vad som saknas eller skaver för rollen. */
  gap?: string;
}

export interface ScenarioProfile {
  id: string;
  title: string;
  size: string;
  summary: string;
  keyFactors: string[];
  /** Skeden som profilen löper genom. */
  phases: string[];
  /** Dokumentnummer som används i profilen. */
  docs: string[];
  /** Referensprojekt som körs idé → bruksskede med samtliga dokument. */
  fullLifecycle?: boolean;
  perspectives: RolePerspective[];
}

export interface GapItem {
  id: string;
  title: string;
  description: string;
  scenarios: string[];
  priority: "hög" | "medel" | "låg";
  status: "åtgärdat" | "att bygga";
}

/** Händelse i ett scenario: vad som inträffar, var i processen och när. */
export interface ScenarioEvent {
  id: string;
  scenario: string;
  when: string;
  what: string;
  systemResponse: string;
  outcome: "hanteras" | "delvis" | "lucka";
}

/** Alla dokument i systemet, hämtade från numreringsregistret.
 *  Scenariorapporten själv (00c) räknas inte som ett projektdokument. */
export const DOCUMENTS: { docNumber: string; title: string }[] = Object.values(DOC_REGISTRY)
  .filter((d) => d.docNumber !== "00c")
  .map((d) => ({ docNumber: d.docNumber, title: d.title }))
  .sort((a, b) => a.docNumber.localeCompare(b.docNumber, "sv"));

/** Samtliga dokumentnummer – används av de två fullständiga livscykelprojekten. */
const ALL_DOCS = DOCUMENTS.map((d) => d.docNumber);

const p = (
  role: AuditRole,
  coverage: Coverage,
  works: string,
  gap?: string
): RolePerspective => ({ role, coverage, works, gap });

const FULL_PHASES = [
  "Idé och behov",
  "Program",
  "System",
  "Detalj",
  "Upphandling",
  "Bygg",
  "Slutskede",
  "Förvaltning",
];

export const SCENARIOS: ScenarioProfile[] = [
  {
    id: "s1",
    title: "Universitetssjukhus – forskningslabb och ny kulvert",
    size: "1,6 mdkr · 5 etapper · 54 månader · 27 entreprenörer · 8 projektörer",
    summary:
      "Referensprojekt 1 i omgång 6. Idé → förvaltning genom samtliga dokument. Labbmiljö med biosäkerhet och strålkällor, kulvertarbete under drift och åtta projektörer som lämnar egna underlag.",
    keyFactors: [
      "Idé → förvaltning",
      "Samtliga dokument",
      "Biosäkerhet och strålkällor",
      "Kulvert under drift",
      "8 projektörer",
    ],
    phases: FULL_PHASES,
    docs: ALL_DOCS,
    fullLifecycle: true,
    perspectives: [
      p("Byggherre", "klart", "Portföljen och programöversikten visar alla fem etapper, öppna tillbud, saknade roller och kvitterade undantag utan att öppna enskilda dokument."),
      p("Projektör", "klart", "Var och en av de åtta projektörerna har egen rutin (Dok 18) och leveransintyg (Dok 19) med räknare över kvarvarande risker."),
      p("Bas-P", "klart", "Dok 30c väger ihop åtta inlämningar och flaggar olika risknivå på samma risk; Dok 10b jämför utredningarna per område."),
      p("Bas-U", "klart", "Beredningar, startbesked, ronder och tillbud ligger på samma tidslinje och följer etappens datum vid förskjutning."),
      p("Entreprenör", "klart", "Portalen ger AMP, ordningsregler, labbintroduktion, SDS-inlämning och kvittens av startbesked."),
      p("Projektledare", "klart", "Granskningsöversikten filtreras på roll, skede och period med sign-off eller returnering."),
      p("Uppdragsledare", "klart", "Läsläge över hela kedjan; dagslägesrapporten (Dok 06c) ger ett samlat bevisunderlag vid valt datum."),
      p("Förvaltare", "klart", "Bruksrisker, överlämning och uppföljning med intervall landar samlat när överlämningsgrinden passerats."),
    ],
  },
  {
    id: "s2",
    title: "Järnvägsstation – nytt resecentrum över spår i trafik",
    size: "1,1 mdkr · 6 etapper · 46 månader · spårnära arbete",
    summary:
      "Referensprojekt 2 i omgång 6. Full kedja idé → förvaltning med tågtrafik under bygget, spänningsfrånkoppling av kontaktledning, nattfönster och fyra externa projektörer som levererar in utifrån.",
    keyFactors: [
      "Idé → förvaltning",
      "Samtliga dokument",
      "Spårnära arbete och kontaktledning",
      "Tredje man: resenärer",
      "Externa projektörsleveranser",
    ],
    phases: FULL_PHASES,
    docs: ALL_DOCS,
    fullLifecycle: true,
    perspectives: [
      p("Byggherre", "klart", "Tredjemansplan, förhandsanmälan och grindstatus syns i portföljen med namn, roll och tidpunkt."),
      p("Projektör", "klart", "Bro- och spårprojektörernas ZIP-leveranser kan läsas av mottagaren via publika läsvyn utan konto."),
      p("Bas-P", "klart", "Fyra externa leveranser vägs samman i Dok 30c med beslutsrad; konflikter mellan spår, el och mark listas separat."),
      p("Bas-U", "klart", "Startbesked blockeras i zoner med öppen frånkoppling och kräver kvitterad spårvakt i beredningen."),
      p("Entreprenör", "klart", "Huvudentreprenören ser upprullad status för alla UE-led: kvitterad AMP, ordningsregler och giltiga behörighetsbevis."),
      p("Projektledare", "klart", "Granskar per etapp och nattfönster inför varje trafikpåsläpp."),
      p("Uppdragsledare", "klart", "Läsläge på hela kedjan inklusive sign-off-historik och kvitterade undantag."),
      p("Förvaltare", "klart", "Tar emot etapp för etapp med eget uppföljningsintervall per anläggningsdel."),
    ],
  },
  {
    id: "s3",
    title: "Skola i drift – tillbyggnad med elever kvar",
    size: "95 MSEK · 3 etapper · 20 månader · verksamhet dygnet runt vardagar",
    summary:
      "Skolgård och entré delas med barn under hela bygget. Testar tredjemansplan, avspärrning, transportvägar och att en kommunal byggherre klarar processen med intern projektledare.",
    keyFactors: ["Barn som tredje man", "Etappvisa transportvägar", "Kommunal byggherre"],
    phases: ["System", "Detalj", "Upphandling", "Bygg", "Slutskede", "Förvaltning"],
    docs: ["00b", "01", "03", "04", "05", "06", "07", "10", "11", "12", "13", "15", "15c", "16", "17", "20", "21", "22", "23", "24", "24b", "25", "26", "27", "28", "30", "30b", "31", "32", "33", "34"],
    perspectives: [
      p("Byggherre", "klart", "Ser att tredjemansplanen är avstämd inför varje etappbyte och vad som stoppar nästa etapp."),
      p("Projektör", "klart", "Utrymningskrav under byggtiden lämnas vidare som funktionskrav till bruksskedet."),
      p("Bas-P", "klart", "Etappbyte flaggar tredjemansplanen automatiskt för ny avstämning."),
      p("Bas-U", "klart", "Beredningar för transport in på skolgård kräver kvitterad tidsplan mot skolans schema."),
      p("Entreprenör", "klart", "Portalen ger regler för arbete nära barn och kvittens per person."),
      p("Projektledare", "klart", "Granskar per etapp och termin."),
      p("Uppdragsledare", "klart", "Läsläge med sign-off och framflyttade frågor."),
      p("Förvaltare", "klart", "Bruksrisker för takarbete och ventilation följs upp med intervall efter inflyttning."),
    ],
  },
  {
    id: "s4",
    title: "Kraftvärmeverk – revisionsstopp på fyra veckor",
    size: "220 MSEK · 1 etapp · 4 veckors stopp · 600 personer samtidigt",
    summary:
      "Extremt komprimerat tidsfönster med heta arbeten, slutna utrymmen, asbest i isolering och hundratals entreprenörer på samma yta. Testar startbesked i hög takt och SDS-registret.",
    keyFactors: ["Revisionsstopp", "Heta arbeten", "Asbest", "Slutna utrymmen", "Hög personaltäthet"],
    phases: ["Detalj", "Upphandling", "Bygg", "Slutskede"],
    docs: ["03", "04", "05", "06", "06c", "07", "08", "09", "10", "11", "12", "13", "15", "15c", "16", "17", "20", "21", "23", "24", "24b", "25", "26", "27", "28", "30", "30b", "30c", "34"],
    perspectives: [
      p("Byggherre", "klart", "Ser dag för dag vilka moment som har startbesked och vilka som väntar på underlag."),
      p("Projektör", "klart", "Kvarvarande risker för isolering och rörgenomföringar förs vidare med källa."),
      p("Bas-P", "klart", "Asbestgrinden låser moment tills saneringsintyg registrerats."),
      p("Bas-U", "klart", "Startbesked i hög takt med kvitterad gasmätning och heta arbeten-tillstånd per moment."),
      p("Entreprenör", "klart", "Lämnar SDS i beredningen och de hamnar direkt i projektets samlingsdokument."),
      p("Projektledare", "klart", "Granskar per dygn med dagslägesrapport som stämpel."),
      p("Uppdragsledare", "klart", "Läsläge på tillbud och stoppade moment."),
      p("Förvaltare", "klart", "Ej tillämpligt – anläggningen återgår till driftorganisationen via Dok 22."),
    ],
  },
  {
    id: "s5",
    title: "Vindkraftpark – 14 verk i skogsterräng",
    size: "1,4 mdkr · 3 etapper · 30 månader · geografiskt spridda arbetsområden",
    summary:
      "Arbetsområdena ligger mil ifrån varandra med egna vägar, lyft på hög höjd och långa insatstider för räddning. Testar arbetsområdesutredning per läge och samordning på distans.",
    keyFactors: ["Spridda arbetsområden", "Höghöjdsarbete", "Lång räddningsinsats", "Vägar och transporter"],
    phases: ["System", "Detalj", "Upphandling", "Bygg", "Slutskede", "Förvaltning"],
    docs: ["02", "03", "04", "05", "06", "07", "10", "10b", "11", "12", "13", "15", "15c", "16", "17", "18", "19", "20", "21", "22", "23", "24", "25", "26", "27", "30", "30b", "30c", "31", "32", "33"],
    perspectives: [
      p("Byggherre", "klart", "Programöversikten visar varje verk som eget område med status och blockeringar."),
      p("Projektör", "klart", "Fundament- och elprojektör lämnar egna leveransintyg per läge."),
      p("Bas-P", "klart", "Dok 10b slår ihop 14 arbetsområdesutredningar till en samlad bild med spårbar källa per läge."),
      p("Bas-U", "klart", "Beredning för tornlyft kräver kvitterad räddningsplan och väderkriterier."),
      p("Entreprenör", "klart", "Montagelaget ser sitt läge, sina beredningar och gällande väderstopp."),
      p("Projektledare", "klart", "Granskar per läge och etapp."),
      p("Uppdragsledare", "klart", "Läsläge på hela parken."),
      p("Förvaltare", "klart", "Bruksrisker för service på höjd överlämnas per verk med intervall."),
    ],
  },
  {
    id: "s6",
    title: "Kulturmärkt byggnad – varsam renovering med museidrift",
    size: "48 MSEK · 2 etapper · 18 månader · antikvariska krav",
    summary:
      "Trånga utrymmen, blyfärg och gamla installationer i en byggnad som är öppen för besökare. Testar farligt material, dispenser och att antikvariska krav krockar med skyddsåtgärder.",
    keyFactors: ["Blyfärg och farligt material", "Besökare i huset", "Antikvariska krav", "Trånga utrymmen"],
    phases: ["Detalj", "Upphandling", "Bygg", "Slutskede", "Förvaltning"],
    docs: ["01", "03", "05", "07", "08", "09", "10", "11", "12", "13", "15", "16", "17", "20", "21", "22", "23", "24", "25", "26", "27", "28", "30", "30b", "31", "33"],
    perspectives: [
      p("Byggherre", "klart", "Ser vilka utrymmen som är låsta av farligt material och vad som krävs för att öppna dem."),
      p("Projektör", "klart", "Antikvariens och konstruktörens motstridiga krav dokumenteras som kvarvarande risk med beslut."),
      p("Bas-P", "klart", "Beslut om avsteg loggas med namn, roll och skede."),
      p("Bas-U", "klart", "Startbesked kräver kvitterad blysanering och luftmätning."),
      p("Entreprenör", "klart", "Portalen ger regler för arbete med besökare i huset."),
      p("Projektledare", "klart", "Granskar per rum och etapp."),
      p("Uppdragsledare", "klart", "Läsläge med kvitterade avsteg."),
      p("Förvaltare", "klart", "Kvarvarande blyrisk vid framtida underhåll förs över till bruksskedet."),
    ],
  },
  {
    id: "s7",
    title: "Installationskonsult – levererar till tre projekt utanför systemet",
    size: "Tre uppdrag · 0,4–2,2 MSEK · endast projektörsrollen",
    summary:
      "Ett konsultföretag använder systemet enbart för sitt eget projektörsansvar i tre parallella uppdrag hos beställare som inte har systemet. Testar Dok 18/19, ZIP-paketet och publika läsvyn.",
    keyFactors: ["Endast projektörsrollen", "Tre parallella uppdrag", "Externa mottagare", "Publik läsvy"],
    phases: ["System", "Detalj"],
    docs: ["03", "10", "11", "16", "18", "19", "30b", "31"],
    perspectives: [
      p("Byggherre", "klart", "Beställaren saknar konto men öppnar leveransen på /leverans/<kod> och ser innehåll, riskräkning och giltighet."),
      p("Projektör", "klart", "En rutin (Dok 18) återanvänds i alla tre uppdragen medan leveransintyget (Dok 19) är unikt per uppdrag."),
      p("Bas-P", "klart", "Ej tillämpligt – rollen ligger hos mottagaren."),
      p("Bas-U", "klart", "Ej tillämpligt i uppdragen."),
      p("Entreprenör", "klart", "Ej tillämpligt i uppdragen."),
      p("Projektledare", "klart", "Uppdragsledningen granskar och signerar varje leveransintyg före utskick."),
      p("Uppdragsledare", "klart", "Läsläge och sign-off på leveranserna."),
      p("Förvaltare", "klart", "Ej tillämpligt."),
    ],
  },
  {
    id: "s8",
    title: "Samfällighet – vägbro och VA med inhyrd projektledare",
    size: "9 MSEK · 1 etapp · 7 månader · ovan byggherre",
    summary:
      "En samfällighetsförening är byggherre utan erfarenhet och hyr in projektledare och Bas-P. Testar lättläget, inbjudningsmallarna och att en oerfaren beställare klarar sig utan hjälp.",
    keyFactors: ["Ovan byggherre", "Lättläge", "Inhyrda roller", "Trafik under bygget"],
    phases: ["Detalj", "Upphandling", "Bygg", "Slutskede"],
    docs: ["01", "03", "05", "07", "10", "12", "13", "15", "17", "20", "21", "22", "23", "24", "24b", "26", "27", "28", "30", "30b", "31"],
    perspectives: [
      p("Byggherre", "klart", "Nästa-steg-kortet säger exakt vad styrelsen ska göra härnäst och lättläget döljer det som inte gäller."),
      p("Projektör", "klart", "Brokonstruktören lämnar in via leveransintyg."),
      p("Bas-P", "klart", "Inhyrd Bas-P bjuds in med färdig mall och rätt behörighet från start."),
      p("Bas-U", "klart", "Beredningar för trafikavstängning och schakt med AI-stöd."),
      p("Entreprenör", "klart", "Portalen ger regler och kvittens utan konto i systemet."),
      p("Projektledare", "klart", "Inhyrd projektledare bjuds in med mallen \"Extern projektledare\" – läs- och granskningsrätt med giltighetsdatum."),
      p("Uppdragsledare", "klart", "Ej tillämpligt – rollen tilldelas inte."),
      p("Förvaltare", "klart", "Samfälligheten tar över bruksrisker för broinspektion."),
    ],
  },
  {
    id: "s9",
    title: "Läkemedelsfabrik – renrum byggs medan produktion pågår",
    size: "780 MSEK · 4 etapper · 32 månader · GMP-miljö",
    summary:
      "Renrumsbygge intill validerad produktion med hårda krav på renhet, tryckzoner och behörighet. Testar parallella skeden, byte av Bas-U mitt i projektet och rollhistorik.",
    keyFactors: ["Parallella skeden", "GMP och renrum", "Byte av Bas-U", "Behörighetszoner"],
    phases: ["System", "Detalj", "Upphandling", "Bygg", "Slutskede", "Förvaltning"],
    docs: ["00", "00b", "02", "03", "04", "05", "06", "06c", "09", "10", "10b", "11", "12", "13", "15", "15c", "16", "17", "18", "19", "20", "21", "22", "23", "24", "24b", "25", "26", "27", "28", "30", "30b", "30c", "31", "32", "33", "34"],
    perspectives: [
      p("Byggherre", "klart", "Programöversikten visar vilket skede varje etapp är i och vad som blockerar."),
      p("Projektör", "klart", "Processprojektörens leveransintyg per etapp visar vad som är stängt och vad som lämnas vidare."),
      p("Bas-P", "klart", "Dok 10b visar var validerad zon gränsar mot byggzon."),
      p("Bas-U", "klart", "Byte av Bas-U görs med överlämningsdialog; rollhistoriken visar vem som ansvarade när."),
      p("Entreprenör", "klart", "Behörighet till renrumszon verifieras före tilldelat moment."),
      p("Projektledare", "klart", "Granskar per etapp och skede utan sammanblandning."),
      p("Uppdragsledare", "klart", "Rollhistoriken svarar på vem som var Bas-U i respektive etapp och när."),
      p("Förvaltare", "klart", "Driftsatt etapp följs upp med intervall medan bygget pågår i nästa."),
    ],
  },
  {
    id: "s10",
    title: "Arbetsplatsolycka – fall från ställning, myndighetsutredning",
    size: "Pågående projekt · olycka i etapp 3 · anmälan enligt AML 3 kap 3 a §",
    summary:
      "Extremtest av bevisvärde. Olyckan ska leda till anmälan, AMP-revidering, stoppade startbesked och ett underlag som håller vid tillsyn – hämtat i ett klick för olycksdagen.",
    keyFactors: ["Allvarlig olycka", "Myndighetsanmälan", "Bevis vid datum", "Stoppade moment"],
    phases: ["Bygg"],
    docs: ["00b", "04", "05", "06c", "07", "12", "13", "15", "20", "21", "23", "24", "24b", "25", "26", "27", "28", "30", "30b", "30c", "32", "34"],
    perspectives: [
      p("Byggherre", "klart", "Ser olyckan, den flaggade AMP-revideringen och vilka moment som stoppats."),
      p("Projektör", "klart", "Fallrisken kan spåras till vem som lämnade in den och i vilket skede."),
      p("Bas-P", "klart", "AMP-versionshistoriken visar vilken version som gällde vid olyckan och vad som ändrades efteråt."),
      p("Bas-U", "klart", "Startbesked dras tillbaka och ny beredning krävs innan ställningsarbete återupptas."),
      p("Entreprenör", "klart", "Kvittenser, introduktioner och kompetensbevis finns per person med tidpunkt."),
      p("Projektledare", "klart", "Rollhistoriken svarar på vem som var ansvarig vid tidpunkten."),
      p("Uppdragsledare", "klart", "Dagslägesrapporten (Dok 06c) hämtar hela dagsläget för olycksdagen som ett Word-dokument."),
      p("Förvaltare", "klart", "Ej tillämpligt i utredningen."),
    ],
  },
];

export const EVENTS: ScenarioEvent[] = [
  {
    id: "e1",
    scenario: "s10",
    when: "Tillsyn, tio dagar efter olyckan",
    what: "Myndigheten begär dokumentationen som den såg ut på olycksdagen.",
    systemResponse:
      "Dok 06c genererar dagsläget vid valt datum – AMP-version, gällande risker, beredningar med startbesked, giltiga kompetensbevis, ronder och öppna tillbud – som ett Word-dokument med verifieringskod.",
    outcome: "hanteras",
  },
  {
    id: "e2",
    scenario: "s2",
    when: "Byggskede, UE i fjärde led",
    what: "Huvudentreprenören ska visa att hela UE-ledet kvitterat AMP och ordningsregler.",
    systemResponse:
      "Entreprenörsträdet summerar grenen: antal företag och led, andel kvitterade, giltiga och utgångna kompetensbevis samt lista över vilka som saknar kvittens.",
    outcome: "hanteras",
  },
  {
    id: "e3",
    scenario: "s8",
    when: "Uppstart",
    what: "Styrelsen ska bjuda in en inhyrd projektledare som bara får läsa och granska.",
    systemResponse:
      "Inbjudningsmallen \"Extern projektledare\" sätter roll, läs- och granskningsrätt och giltighetstid i ett steg.",
    outcome: "hanteras",
  },
  {
    id: "e4",
    scenario: "s7",
    when: "Efter leverans till beställare utan konto",
    what: "Mottagaren vill bläddra i leveransen utan att installera eller registrera sig.",
    systemResponse:
      "Verifieringskoden öppnar den publika läsvyn på /leverans/<kod> med innehåll, riskräkning, version och giltighet.",
    outcome: "hanteras",
  },
  {
    id: "e5",
    scenario: "s1",
    when: "Detaljprojektering, etapp 3",
    what: "Två projektörer sätter olika risknivå på samma strålkälla.",
    systemResponse:
      "Dok 30c markerar posten som konflikt och kräver att Bas-P sätter gällande nivå med motivering som loggas.",
    outcome: "hanteras",
  },
  {
    id: "e6",
    scenario: "s5",
    when: "Systemskede",
    what: "Fjorton arbetsområden ska beskrivas var för sig men bedömas samlat.",
    systemResponse:
      "Dok 10b lägger utredningarna sida vid sida per läge och skapar en samlad version med spårbar källa.",
    outcome: "hanteras",
  },
  {
    id: "e7",
    scenario: "s4",
    when: "Revisionsstopp, dygn 6",
    what: "Ett moment kräver heta arbeten intill isolering med misstänkt asbest.",
    systemResponse:
      "Asbestgrinden låser momentet tills provsvar och saneringsintyg registrerats; startbesked kan inte ges under tiden.",
    outcome: "hanteras",
  },
  {
    id: "e8",
    scenario: "s4",
    when: "Revisionsstopp, dygn 11",
    what: "Fem entreprenörer tar in nya kemikalier samma dag.",
    systemResponse:
      "Varje säkerhetsdatablad registreras i sin beredning och hamnar direkt i projektets samlade SDS-register med datum och avsändare.",
    outcome: "hanteras",
  },
  {
    id: "e9",
    scenario: "s9",
    when: "Byggskede, etapp 2",
    what: "Bas-U byts mitt i etappen när entreprenören byter platschef.",
    systemResponse:
      "Överlämningsdialogen kräver kvittens av den nya rollinnehavaren och rollhistoriken visar exakt vem som ansvarade vilka datum.",
    outcome: "hanteras",
  },
  {
    id: "e10",
    scenario: "s9",
    when: "Bygg etapp 2, projektering etapp 4",
    what: "Samma projekt är i två skeden samtidigt.",
    systemResponse:
      "Skede sätts per etapp; framflyttade frågor i etapp 4 blockerar inte produktionen i etapp 2 men syns i programöversikten.",
    outcome: "hanteras",
  },
  {
    id: "e11",
    scenario: "s3",
    when: "Byggskede, etappbyte 1 → 2",
    what: "Transportvägen flyttas över skolgården.",
    systemResponse:
      "Tredjemansplanen flaggas automatiskt för avstämning tills någon markerar den avstämd med namn och datum.",
    outcome: "hanteras",
  },
  {
    id: "e12",
    scenario: "s6",
    when: "Byggskede, sal 4",
    what: "Blyhalt över gränsvärde upptäcks i befintlig färg.",
    systemResponse:
      "Farligt material-registret låser utrymmet, kräver saneringsbeslut och skapar en revideringsflagga på AMP.",
    outcome: "hanteras",
  },
  {
    id: "e13",
    scenario: "s5",
    when: "Byggskede, verk 9",
    what: "Tornlyftet skjuts två veckor på grund av vind.",
    systemResponse:
      "Etappens datum ändras och systemet föreslår att kopplade ronder och samordningsmöten flyttas lika många dagar.",
    outcome: "hanteras",
  },
  {
    id: "e14",
    scenario: "s2",
    when: "Slutskede, etapp 4",
    what: "Överlämning begärs innan slutkontroll och relationshandlingar är klara.",
    systemResponse:
      "Överlämningsgrinden listar saknat underlag och kräver motivering som loggas med namn, roll och skede.",
    outcome: "hanteras",
  },
  {
    id: "e15",
    scenario: "s1",
    when: "Förvaltning, etapp 1 i drift",
    what: "Uppföljningen av bruksskedet passerar sitt intervall utan att någon agerar.",
    systemResponse:
      "Posten listas som försenad i förvaltarvyn med intervall, ansvarig och antal dagar över tiden.",
    outcome: "hanteras",
  },
  {
    id: "e16",
    scenario: "s6",
    when: "Detaljprojektering",
    what: "En fråga kan inte besvaras förrän rivningen är påbörjad i byggskedet.",
    systemResponse:
      "Frågan flyttas fram med motivering, avsändare och målskede; skedet kan inte stängas förrän beslutet är taget.",
    outcome: "hanteras",
  },
  {
    id: "e17",
    scenario: "s4",
    when: "Revisionsstopp, dygn 1",
    what: "600 personer ska introduceras på två dagar.",
    systemResponse:
      "Portalen tar kvittens per person via länk, men gruppintroduktion med samlad närvarolista måste registreras manuellt.",
    outcome: "delvis",
  },
];

export const GAPS: GapItem[] = [
  {
    id: "g1",
    title: "Gemensamt numreringsregister",
    description:
      "Åtgärdat: all numrering läses från docRegistry, både i processlistan, exporterna och den här matrisen.",
    scenarios: ["s1", "s2"],
    priority: "hög",
    status: "åtgärdat",
  },
  {
    id: "g2",
    title: "En AMP per projekt med etappmärkta bilagor",
    description:
      "Åtgärdat: en arbetsmiljöplan för hela projektet; bilagorna märks med etapp och område i både app och Word-export.",
    scenarios: ["s1", "s9"],
    priority: "hög",
    status: "åtgärdat",
  },
  {
    id: "g3",
    title: "Spärr för framflyttade frågor vid skedesbyte",
    description:
      "Åtgärdat: skedet kan inte stängas med öppna framflyttade frågor. Förlängning kräver ny motivering som loggas.",
    scenarios: ["s1", "s9"],
    priority: "hög",
    status: "åtgärdat",
  },
  {
    id: "g4",
    title: "Tredjemansplan flaggas vid etappbyte",
    description:
      "Åtgärdat: Dok 07 flaggas automatiskt för avstämning när etappindelningen ändras.",
    scenarios: ["s5", "s2"],
    priority: "medel",
    status: "åtgärdat",
  },
  {
    id: "g5",
    title: "Uppföljning bruksskede med intervall och påminnelse",
    description:
      "Åtgärdat: intervall 3/6/12/24 månader, kvittens med namn och datum samt lista över försenade uppföljningar.",
    scenarios: ["s1", "s9"],
    priority: "medel",
    status: "åtgärdat",
  },
  {
    id: "g6",
    title: "Projektörens eget ansvar kan bevisas utåt",
    description:
      "Åtgärdat i omgång 5: Dok 18 (arbetsmiljörutin) och Dok 19 (leveransintyg) plus ZIP-paket gör att en projektör kan täcka och visa sitt ansvar mot ett projekt som inte använder systemet.",
    scenarios: ["s7", "s2"],
    priority: "hög",
    status: "åtgärdat",
  },
  {
    id: "g7",
    title: "Flera projektörers risker blir ett dokument",
    description:
      "Åtgärdat i omgång 5: Dok 30c slår ihop interna och inlämnade risksammanställningar, markerar konflikter i risknivå och kräver beslutsrad från Bas-P.",
    scenarios: ["s1", "s3"],
    priority: "hög",
    status: "åtgärdat",
  },
  {
    id: "g8",
    title: "Flera arbetsområdesutredningar jämförs och slås ihop",
    description:
      "Åtgärdat i omgång 5: Dok 10b visar utredningarna sida vid sida per område och skapar en samlad version med spårbar källa.",
    scenarios: ["s1", "s9"],
    priority: "medel",
    status: "åtgärdat",
  },
  {
    id: "g9",
    title: "Samlat register för säkerhetsdatablad",
    description:
      "Åtgärdat: SDS lämnas i arbetsberedningen och hamnar automatiskt i projektets samlingsdokument med datum och avsändare.",
    scenarios: ["s4", "s6"],
    priority: "medel",
    status: "åtgärdat",
  },
  {
    id: "g10",
    title: "Parallella skeden per etapp",
    description:
      "Åtgärdat: skede sätts per etapp så att produktion i en etapp inte blockeras av öppna frågor i en annan.",
    scenarios: ["s9", "s1"],
    priority: "medel",
    status: "åtgärdat",
  },
  {
    id: "g11",
    title: "Ingen samlad dagslägesrapport vid tillsyn",
    description:
      "Åtgärdat: Dok 06c Dagsläge vid datum sammanställer AMP-version, risker, arbetsberedningar, giltiga bevis, öppna tillbud och förfallna besiktningar vid valt datum – och laddas ner som ett Word-dokument i ett klick.",
    scenarios: ["s10", "s2"],
    priority: "låg",
    status: "åtgärdat",
  },
  {
    id: "g12",
    title: "Kvittenser rullas inte upp i entreprenörsträdet",
    description:
      "Åtgärdat: entreprenörsvyn summerar nu varje gren – antal företag, antal led, andel kvitterade AMP och ordningsregler samt kompetensbevis och utgångna bevis, med lista över vilka som saknar kvittens.",
    scenarios: ["s2", "s6"],
    priority: "låg",
    status: "åtgärdat",
  },
  {
    id: "g13",
    title: "Mall för inhyrd granskarroll",
    description:
      "Åtgärdat: snabbmallar i roll- och behörighetsvyn för inhyrd granskare, extern projektledare, vikarierande Bas-P/Bas-U och förvaltare med förvald roll, behörighet och giltighet.",
    scenarios: ["s8", "s3"],
    priority: "låg",
    status: "åtgärdat",
  },
  {
    id: "g14",
    title: "Läsvy för extern mottagare av projektörspaket",
    description:
      "Åtgärdat: publik läsvy på /leverans/<kod> visar innehåll, riskräkning och giltighet för projektörspaketet utan konto. Länken står i LAS_MIG-filen och nås direkt från verifieringssidan.",
    scenarios: ["s7", "s2"],
    priority: "låg",
    status: "åtgärdat",
  },
  {
    id: "g15",
    title: "Gruppintroduktion med samlad närvarolista",
    description:
      "Ny i omgång 6: vid revisionsstopp introduceras hundratals personer samtidigt. Kvittens sker idag per person via portalen; en samlad närvarolista för gruppintroduktion får registreras manuellt.",
    scenarios: ["s4", "s2"],
    priority: "låg",
    status: "att bygga",
  },
];


/** Hur många av projekten som använder respektive dokument. */
export const DOCUMENT_COVERAGE = () =>
  DOCUMENTS.map((d) => {
    const used = SCENARIOS.filter((s) => s.docs.includes(d.docNumber));
    return {
      ...d,
      count: used.length,
      scenarios: used.map((s) => s.title),
      ok: used.length >= 2,
    };
  });

export const COVERAGE_SUMMARY = () => {
  const all = SCENARIOS.flatMap((s) => s.perspectives);
  const klart = all.filter((p) => p.coverage === "klart").length;
  const docs = DOCUMENT_COVERAGE();
  return {
    total: all.length,
    klart,
    delvis: all.filter((p) => p.coverage === "delvis").length,
    saknas: all.filter((p) => p.coverage === "saknas").length,
    percent: Math.round((klart / all.length) * 100),
    docsTotal: docs.length,
    docsOk: docs.filter((d) => d.ok).length,
    docsThin: docs.filter((d) => !d.ok).map((d) => `Dok ${d.docNumber}`),
  };
};
